function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeout);
      reject(new Error("Aborted"));
    };

    if (signal?.aborted) {
      clearTimeout(timeout);
      reject(new Error("Aborted"));
      return;
    }

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function isAbortError(error: unknown): boolean {
  const abortError = error as { code?: string; name?: string };

  return Boolean(
    abortError?.code === "ERR_CANCELED" ||
      abortError?.name === "CanceledError" ||
      abortError?.name === "AbortError",
  );
}

class AttemptTimeoutError extends Error {}

function withAttemptTimeout<T>(
  timeoutMs: number,
  externalSignal: AbortSignal | undefined,
  request: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const attemptController = new AbortController();
  let timeout: ReturnType<typeof setTimeout>;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      attemptController.abort();
      reject(new AttemptTimeoutError());
    }, timeoutMs);
  });

  const onExternalAbort = () => attemptController.abort();
  if (externalSignal?.aborted) {
    attemptController.abort();
  } else {
    externalSignal?.addEventListener("abort", onExternalAbort, {
      once: true,
    });
  }

  return Promise.race([request(attemptController.signal), timeoutPromise]).finally(
    () => {
      clearTimeout(timeout);
      externalSignal?.removeEventListener("abort", onExternalAbort);
    },
  );
}

function isClientError(error: unknown): boolean {
  const httpError = error as { response?: { status?: number } };
  const status = httpError?.response?.status;

  return Boolean(status && status >= 400 && status < 500);
}

export interface PollUntilBackoff {
  multiplier: number;
  maxIntervalMs: number;
}

export interface PollUntilOptions<T> {
  request: (signal: AbortSignal) => Promise<T>;
  isReady: (result: T) => boolean;
  timeoutMs: number;
  intervalMs: number;
  backoff?: PollUntilBackoff;
  signal?: AbortSignal;
  onPollAttempt?: () => void;
}

export async function pollUntil<T>(
  options: PollUntilOptions<T>,
): Promise<boolean> {
  const deadline = Date.now() + options.timeoutMs;
  let currentIntervalMs = options.intervalMs;

  while (Date.now() < deadline) {
    if (options.signal?.aborted) {
      return false;
    }

    const remainingForAttempt = deadline - Date.now();
    if (remainingForAttempt <= 0) {
      break;
    }

    try {
      const result = await withAttemptTimeout(
        remainingForAttempt,
        options.signal,
        options.request,
      );
      if (options.isReady(result)) {
        return true;
      }
    } catch (err) {
      if (options.signal?.aborted) {
        return false;
      }

      if (err instanceof AttemptTimeoutError) {
      } else if (isAbortError(err)) {
        return false;
      } else if (isClientError(err)) {
        throw err;
      }
    }

    options.onPollAttempt?.();

    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      break;
    }

    try {
      await sleep(Math.min(currentIntervalMs, remaining), options.signal);
    } catch {
      return false;
    }

    if (options.backoff) {
      currentIntervalMs = Math.min(
        currentIntervalMs * options.backoff.multiplier,
        options.backoff.maxIntervalMs,
      );
    }
  }

  return false;
}
